import axios from 'axios';
import { API_URL } from '../config/api';

export interface ReferralInfo {
  personalPromoCode: string;
  referralLink: string;
  balance: number;
  program: 'Personal' | 'Professional';
  appliedPromoCode?: string;
  canWithdraw: boolean;
  minWithdrawalAmount: number;
}

export interface ReferralTransaction {
  id: string;
  amount: number;
  type: 'Earning' | 'Withdrawal';
  description: string;
  dateCreated: string;
  referralUserEmail?: string;
}

export interface PromoCodeValidation {
  isValid: boolean;
  discountPercent: number;
  message: string;
}

export interface ProfessionalApplicationRequest {
  phoneNumber: string;
  message: string;
}

export interface WithdrawalRequest {
  amount: number;
}


const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const referralApi = {
  // Получение информации о реферальной программе пользователя
  async getReferralInfo(): Promise<ReferralInfo> {
    const response = await axios.get(`${API_URL}/referral/info`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Получение истории реферальных транзакций
  async getReferralHistory(): Promise<ReferralTransaction[]> {
    const response = await axios.get(`${API_URL}/referral/history`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Валидация промокода
  async validatePromoCode(code: string): Promise<PromoCodeValidation> {
    const response = await axios.get(`${API_URL}/promocodes/validate/${code}`);
    return response.data;
  },

  // Подача заявки на профессиональную программу
  async applyForProfessionalProgram(request: ProfessionalApplicationRequest): Promise<{ applicationId: string }> {
    const response = await axios.post(`${API_URL}/referral/apply-professional`, request, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Создание заявки на вывод средств
  async createWithdrawalRequest(request: WithdrawalRequest): Promise<{ requestId: string }> {
    const response = await axios.post(`${API_URL}/referral/withdraw`, request, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Симуляция платежа (для тестирования)
  async simulatePayment(data: {
    tariffPlanId: string;
    amount: number;
    teamSize?: number;
    teamName?: string;
  }): Promise<{ success: boolean; subscriptionId: string; appliedDiscount: number; paymentId: string }> {
    const response = await axios.post(`${API_URL}/payment/simulate-payment`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Утилитарные функции для работы с cookie
export const referralUtils = {
  // Получение промокода из cookie
  getReferralPromoCodeFromCookie(): string | null {
    const name = 'referralPromoCode=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  },

  // Удаление промокода из cookie
  clearReferralPromoCode(): void {
    document.cookie = 'referralPromoCode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  },

  // Сохранение промокода в cookie
  setReferralPromoCode(promoCode: string, days: number = 15): void {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    
    document.cookie = `referralPromoCode=${promoCode}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
  },

  // Копирование ссылки в буфер обмена
  async copyReferralLink(link: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch (err) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    }
  }
}; 